/**
 * OBA-Core ControlPanel WebApp エントリーポイント
 *
 * - 起動時に `/config.json` を fetch し、Cognito UserPool / API URL を初期化する。
 *   ローカル開発時は `web-app/public/config.json`、本番では CDK が S3 に配置した
 *   `/config.json` (CloudFront 経由) が読み込まれる。
 * - 描画系の例外で「白いページ」になるのを避けるため、ErrorBoundary で全体を包む。
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { loadAppConfig, type AppConfig } from './config';
import './styles.css';

function renderFatal(root: HTMLElement, title: string, message: string): void {
  root.innerHTML = `<div style="padding: 24px; color: #b00020; font-family: sans-serif; max-width: 720px; margin: 40px auto;">
    <h1>${title}</h1>
    <p>${message}</p>
    <p>web-app/public/config.json が正しく配置されているか、もしくは本番では CloudFront 経由で /config.json が配信されているかを確認してください。</p>
  </div>`;
}

async function bootstrap(): Promise<void> {
  const root = document.getElementById('root');
  if (!root) {
    // body 直下に表示できる程度の最低限のフォールバック
    document.body.innerHTML =
      '<div style="padding:24px;color:#b00020;font-family:sans-serif">#root 要素が見つからないため起動できません。</div>';
    return;
  }

  let config: AppConfig;
  try {
    config = await loadAppConfig();
  } catch (e) {
    // 設定ファイルが取得できない場合は致命的エラーとして表示。
    // 本番では起こらない想定だが、ローカル開発時の設定漏れに備える。
    renderFatal(root, '設定ファイルの読み込みに失敗しました', (e as Error).message);
    return;
  }

  try {
    createRoot(root).render(
      <StrictMode>
        <ErrorBoundary>
          <BrowserRouter>
            <AuthProvider config={config}>
              <App config={config} />
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </StrictMode>,
    );
  } catch (e) {
    // createRoot 直後の同期的な例外も「白ページ」を防ぐためここで握り潰してメッセージ表示する。
    renderFatal(root, 'アプリケーションの初期化に失敗しました', (e as Error).message);
  }
}

void bootstrap();
